/**
 * tariffs/west-bengal.ts — WBSEDCL B-IDIT Tariff (West Bengal)
 * ==============================================================
 * Source: Invoice 641500195104 · MAR 2026 · Consumer 900007553
 * Reference: VIREON-CORTEX Variables & Formulas PDF (all sections)
 * Reference: WBSEDCL Tariff Order 2025-26 (Load Factor & Power Factor tables)
 *
 * Supply Voltage categories (WBSEDCL tariff order):
 *   • "Below 33 kV" → 0.4 kV and 11 kV consumers (Taparia: 0.4 kV, Omega/JD Powercorn: 11 kV)
 *   • "33 kV"       → 33 kV consumers
 *   • "Above 33 kV" → 132 kV consumers
 *
 * PF Rebate/Surcharge applies only to consumers under TOD Tariff
 * (Contract Demand > 50 kVA). For consumers under non-TOD Tariff
 * (Contract Demand ≤ 50 kVA), only the "non-TOD" column applies.
 *
 * All three WB factories (Taparia 0.4 kV, Omega 11 kV, JD Powercorn 11 kV)
 * have Contract Demand > 50 kVA, so TOD Tariff PF columns apply.
 *
 * Category: B-IDIT, 0.4 kV / 11 kV (Industrial, Demand ≥ 75 kW)
 * Energy billing unit: kWh (active energy)
 * Demand minimum: 85% of Contract Demand
 *
 * DO NOT hardcode these values elsewhere.
 * For tariff revisions, update ONLY this file.
 */

import type { StateTariff, ToDPeriod, PfAdjustmentRow, LfRebateRow } from './types';

// ─── ToD ──────────────────────────────────────────────────────────────────────

function todPeriodFor(hour: number): ToDPeriod {
  if (hour >= 6  && hour < 17) return 'NORMAL';
  if (hour >= 17 && hour < 23) return 'PEAK';
  return 'OFF-PEAK';
}

// ─── Energy Rates ─────────────────────────────────────────────────────────────

const ENERGY_RATES: Record<ToDPeriod, number> = {
  'OFF-PEAK': 5.12,  // 512 paise/kWh — 23:00–06:00
  'NORMAL':   6.83,  // 683 paise/kWh — 06:00–17:00
  'PEAK':     8.20,  // 820 paise/kWh — 17:00–23:00
};

// WBSEDCL B-IDIT is a flat (single-tier) tariff — no MD-based tier multipliers.
function getTariffRates(_mdKva: number, _cdKva: number): Record<ToDPeriod, number> {
  return ENERGY_RATES;
}

function getTierLabel(_mdKva: number, _cdKva: number): string {
  return 'WBSEDCL B-IDIT (flat)';
}

// ─── PF Adjustment ────────────────────────────────────────────────────────────
//
// Source: WBSEDCL Tariff Order 2025-26, Power Factor Rebate & Surcharge table.
// Applies to HV, EHV and L&MV (above 50 kVA) consumers — i.e. all WB factories.
// All WB factories have Contract Demand > 50 kVA → TOD Tariff columns apply.
//
// Positive values = rebate (deducted from energy charge).
// Negative values = surcharge (added to energy charge).
// Values are % of energy charge for the respective ToD period.
//
// Note on structure: rows with surcharge = 0 in the "Normal" column and
// non-zero in "Peak" (or vice-versa) are correct per the tariff order —
// surcharge only applies in peak/off-peak for some PF ranges.

const PF_ADJUSTMENT_TABLE: PfAdjustmentRow[] = [
  // ── Rebate bands (PF > 0.92) ────────────────────────────────────────────
  { minPf: 0.99, maxPf: Infinity, normalPct: +8.00, peakPct: +9.00, offPeakPct: +7.00 },
  { minPf: 0.98, maxPf: 0.99,     normalPct: +7.00, peakPct: +8.00, offPeakPct: +6.00 },
  { minPf: 0.97, maxPf: 0.98,     normalPct: +5.00, peakPct: +6.00, offPeakPct: +4.00 },
  { minPf: 0.96, maxPf: 0.97,     normalPct: +4.00, peakPct: +5.00, offPeakPct: +3.00 },
  { minPf: 0.95, maxPf: 0.96,     normalPct: +3.00, peakPct: +4.00, offPeakPct: +2.00 },
  { minPf: 0.94, maxPf: 0.95,     normalPct: +2.25, peakPct: +3.00, offPeakPct: +1.50 },
  { minPf: 0.93, maxPf: 0.94,     normalPct: +1.50, peakPct: +2.00, offPeakPct: +1.00 },
  { minPf: 0.92, maxPf: 0.93,     normalPct: +0.75, peakPct: +1.00, offPeakPct: +0.50 },
  // ── Dead-band: no adjustment ─────────────────────────────────────────────
  { minPf: 0.86, maxPf: 0.92,     normalPct:  0.00, peakPct:  0.00, offPeakPct:  0.00 },
  // ── Surcharge bands (PF < 0.86) ─────────────────────────────────────────
  { minPf: 0.85, maxPf: 0.86,     normalPct: -1.00, peakPct: -1.25, offPeakPct: -0.75 },
  { minPf: 0.84, maxPf: 0.85,     normalPct: -2.00, peakPct: -2.50, offPeakPct: -1.50 },
  { minPf: 0.83, maxPf: 0.84,     normalPct: -2.50, peakPct: -3.25, offPeakPct: -1.75 },
  { minPf: 0.82, maxPf: 0.83,     normalPct: -3.00, peakPct: -4.00, offPeakPct: -2.00 },
  { minPf: 0.81, maxPf: 0.82,     normalPct: -4.00, peakPct: -5.00, offPeakPct: -3.00 },
  { minPf: 0.80, maxPf: 0.81,     normalPct: -5.00, peakPct: -6.00, offPeakPct: -4.00 },
  { minPf: 0.00, maxPf: 0.80,     normalPct: -6.00, peakPct: -7.00, offPeakPct: -5.00 },
];

function pfAdjustmentPct(pf: number, period: ToDPeriod): number {
  const row = PF_ADJUSTMENT_TABLE.find(r => pf >= r.minPf && pf < r.maxPf)
    ?? PF_ADJUSTMENT_TABLE[PF_ADJUSTMENT_TABLE.length - 1];
  if (period === 'NORMAL')   return row.normalPct;
  if (period === 'PEAK')     return row.peakPct;
  return row.offPeakPct;
}

// ─── Load Factor Rebate ───────────────────────────────────────────────────────
//
// Source: WBSEDCL Tariff Order 2025-26.
// Applies to HV, EHV and L&MV (above 50 kVA) consumers.
//
// All three WB factories supply voltage is below 33 kV (0.4 kV and 11 kV),
// so the "Below 33 kV" column rates apply to all WB factories.
//
// Negative rebateRsPerKwh = surcharge (added to bill).
// Positive rebateRsPerKwh = rebate (deducted from bill).
//
// LF Surcharge (paise/kWh) for Below 33 kV:
//   0–25%   : 35 paise surcharge
//   25–30%  : 15 paise surcharge
//   30–60%  : 14 paise surcharge (note: one band, not split at 40%)
//   60–65%  :  7 paise surcharge
//
// LF Rebate (paise/kWh) for Below 33 kV:
//   70–75%  :  6 paise rebate
//   75–80%  : 11 paise rebate
//   80–85%  : 16 paise rebate
//   85–90%  : 21 paise rebate
//   90–92%  : 26 paise rebate
//   92–95%  : 31 paise rebate
//   Above 95: 36 paise rebate
//
// 65–70% and 40-65% — no adjustment (dead-band).

const LF_REBATE_TABLE: LfRebateRow[] = [
  // Surcharge bands (negative = surcharge)
  { minLfPct:  0, maxLfPct:  25, rebatePaisePerKwh: -35, rebateRsPerKwh: -0.35 },
  { minLfPct: 25, maxLfPct:  30, rebatePaisePerKwh: -15, rebateRsPerKwh: -0.15 },
  { minLfPct: 30, maxLfPct:  60, rebatePaisePerKwh: -14, rebateRsPerKwh: -0.14 },
  { minLfPct: 60, maxLfPct:  65, rebatePaisePerKwh:  -7, rebateRsPerKwh: -0.07 },
  // No adjustment dead-band
  { minLfPct: 65, maxLfPct:  70, rebatePaisePerKwh:   0, rebateRsPerKwh:  0.00 },
  // Rebate bands (positive = rebate)
  { minLfPct: 70, maxLfPct:  75, rebatePaisePerKwh:   6, rebateRsPerKwh:  0.06 },
  { minLfPct: 75, maxLfPct:  80, rebatePaisePerKwh:  11, rebateRsPerKwh:  0.11 },
  { minLfPct: 80, maxLfPct:  85, rebatePaisePerKwh:  16, rebateRsPerKwh:  0.16 },
  { minLfPct: 85, maxLfPct:  90, rebatePaisePerKwh:  21, rebateRsPerKwh:  0.21 },
  { minLfPct: 90, maxLfPct:  92, rebatePaisePerKwh:  26, rebateRsPerKwh:  0.26 },
  { minLfPct: 92, maxLfPct:  95, rebatePaisePerKwh:  31, rebateRsPerKwh:  0.31 },
  { minLfPct: 95, maxLfPct: 101, rebatePaisePerKwh:  36, rebateRsPerKwh:  0.36 },
];

function lfRebateRsPerKwh(lfPct: number): number {
  return LF_REBATE_TABLE.find(r => lfPct >= r.minLfPct && lfPct < r.maxLfPct)
    ?.rebateRsPerKwh ?? 0;
}

// ─── Tariff Object ────────────────────────────────────────────────────────────

export const WestBengalTariff: StateTariff = {
  // Identity
  stateCode:        'west-bengal',
  stateName:        'West Bengal',
  discName:         'WBSEDCL',
  discFullName:     'West Bengal State Electricity Distribution Company Ltd.',
  tariffCategory:   'B-IDIT',
  voltageLevel:     '0.4 kV',  // Default; Omega and JD Powercorn are 11 kV (both "Below 33 kV")

  // Energy unit
  energyUnit: 'kwh',

  // ToD
  todWindows: {
    'OFF-PEAK': { label: 'Off-Peak', range: '23:00–06:00' },
    'NORMAL':   { label: 'Normal',   range: '06:00–17:00' },
    'PEAK':     { label: 'Peak',     range: '17:00–23:00' },
  },
  todColors: {
    'OFF-PEAK': '#118AB2',
    'NORMAL':   '#0FB18A',
    'PEAK':     '#F97316',
  },
  todPeriodFor,

  // Energy rates
  energyRates:    ENERGY_RATES,
  getTariffRates,
  getTierLabel,

  // Demand charges
  demandChargeModel:        'single-rate',
  demandChargeRsKva:        270,    // Rs/kVA/month
  demandPenalRsKva:         135,    // Same rate for excess
  minDemandPct:             0.85,   // Min chargeable = 85% of CD
  defaultContractDemandKva: 176,

  // PF — all WB factories have CD > 50 kVA → TOD Tariff PF columns apply.
  // PF rebate/surcharge is % of energy charge per ToD period.
  hasPfAdjustment:    true,
  pfAdjustmentTable:  PF_ADJUSTMENT_TABLE,
  pfAdjustmentPct,
  pfPenaltyThreshold: 0.86,   // Surcharge starts below 0.86
  pfRebateThreshold:  0.92,   // Rebate starts at 0.92 and above
  pfTarget:           0.99,
  pfSustainedFloor:   0.92,

  // LF rebate — "Below 33 kV" rates apply to all three WB factories
  // (Taparia 0.4 kV, Omega 11 kV, JD Powercorn 11 kV all < 33 kV)
  hasLfRebate:      true,
  lfRebateTable:    LF_REBATE_TABLE,
  lfRebateRsPerKwh,

  // Electricity duty: 12.5% of net bill
  electricityDutyModel: 'pct-of-bill',
  electricityDutyValue: 0.125,

  // Fixed charges
  meterRentalRsMonth:     1200,
  customerChargesRsMonth: 0,
  tprFraction:            0.0089,  // 0.89% Timely Payment Rebate
  lpseFraction:           0,       // No LPSC in WBSEDCL

  // Carbon
  carbonKgPerKwh: 0.82,

  // Sensor quality
  voltageDropoutV:    100,
  outlierPercentile:  99.5,
  maxIntegrationGapH: 1,

  // Off-peak demand ceiling: 150% of CD
  offPeakMaxDemandPct: 1.5,
};